import { useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { isItemActive } from "../../SidebarNavigation";
import type { ManagerNavigationSection } from "../types";

type CollapsedNavProps = {
  activeSectionId?: string;
  sections: ManagerNavigationSection[];
};

export const CollapsedNav = ({ activeSectionId, sections }: CollapsedNavProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [openSectionId, setOpenSectionId] = useState("");
  const isOpenedByClick = useRef(false);
  const skipFocusTooltip = useRef(false);

  const handleValueChange = (nextSectionId: string) => {
    const isHoverOpen = !openSectionId && !!nextSectionId && !isOpenedByClick.current;
    isOpenedByClick.current = false;

    if (!isHoverOpen) {
      setOpenSectionId(nextSectionId);
    }
  };

  return (
    <NavigationMenu
      orientation="vertical"
      viewport={false}
      value={openSectionId}
      onValueChange={handleValueChange}
      className="max-w-none flex-none items-stretch justify-start px-2"
    >
      <NavigationMenuList className="flex-col items-start gap-1">
        {sections.map(({ icon: Icon, id, items, labelKey }) => (
          <NavigationMenuItem key={id} value={id}>
            <Tooltip>
              <TooltipTrigger
                asChild
                onFocus={event => {
                  if (skipFocusTooltip.current) {
                    skipFocusTooltip.current = false;
                    event.preventDefault();
                  }
                }}
              >
                <NavigationMenuTrigger
                  aria-label={t(labelKey)}
                  onClick={() => {
                    isOpenedByClick.current = true;
                  }}
                  className={cn(
                    "h-10 w-auto gap-0.5 px-2.5 text-neutral-700 [&>svg:last-child]:hidden",
                    id === activeSectionId && "bg-primary-100 text-primary-700"
                  )}
                >
                  <Icon className="size-5" />
                  <ChevronRight className="size-3 text-muted-foreground" />
                </NavigationMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" hidden={!!openSectionId}>
                {t(labelKey)}
              </TooltipContent>
            </Tooltip>
            <NavigationMenuContent className="min-w-52 group-data-[viewport=false]/navigation-menu:top-0 group-data-[viewport=false]/navigation-menu:left-full group-data-[viewport=false]/navigation-menu:mt-0 group-data-[viewport=false]/navigation-menu:ml-3 group-data-[viewport=false]/navigation-menu:shadow-lg">
              <p className="px-3 pt-1 pb-2 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                {t(labelKey)}
              </p>
              <ul>
                {items.map(item => {
                  const { icon: ItemIcon } = item;

                  return (
                    <li key={item.to}>
                      <NavigationMenuLink
                        asChild
                        active={isItemActive(pathname, item)}
                        onSelect={() => {
                          skipFocusTooltip.current = true;
                        }}
                        className="flex-row items-center gap-3 whitespace-nowrap font-normal text-base"
                      >
                        <Link to={item.to}>
                          <ItemIcon className="size-5 text-current" />
                          {t(item.labelKey)}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  );
                })}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
};
